import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import ErrorBoundary from "./components/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ExpenseForm from "./pages/ExpenseForm";
import ExpenseList from "./pages/ExpenseList";
import CreditList from "./pages/CreditList";
import Reports from "./pages/Reports";
import Setup2FA from "./pages/Setup2FA";
import Admin from "./pages/Admin";
import Misc from "./pages/Misc";
import EmployeeManagement from "./pages/EmployeeManagement";
import SilicaLoads from "./pages/SilicaLoads";
import Investment from "./pages/Investment";
import ProfitReport from "./pages/ProfitReport";
import PendingBills from "./pages/PendingBills";
import InvoicePage from "./pages/InvoicePage";
import Auditor from "./pages/Auditor";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Check if running in native app (Android/iOS)
const isNativeApp = Capacitor.isNativePlatform();

// Check if accessing from business app subdomain (app.mjenterprises.net)
const isBusinessAppDomain = () => {
  const hostname = window.location.hostname;
  // Business app domains - skip landing page and go to login
  const businessDomains = [
    'app.mjenterprises.net',
    'app.mjenterprises.com',
    // Add any other business app subdomains here
  ];
  return businessDomains.some(domain => hostname.includes(domain)) || 
         hostname.startsWith('app.') ||
         isNativeApp;
};

// Determine if we should skip landing page
const skipLandingPage = isBusinessAppDomain();

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Only clear token if it's actually expired/invalid, not on network errors
        if (error.response?.status === 401) {
          const errorDetail = error.response?.data?.detail || '';
          if (errorDetail.includes('expired') || errorDetail.includes('Invalid') || errorDetail.includes('token')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          } else {
            // Generic 401 - clear token
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } else if (!error.response) {
          // Network error - don't logout, let them retry
          // Check if we have cached user data
          const cachedUser = localStorage.getItem('user');
          if (cachedUser) {
            setUser(JSON.parse(cachedUser));
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Disable browser's automatic scroll restoration (critical for iOS)
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Single scroll to top - avoid multiple timers that can block touch events on mobile
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        {/* Only show PWA install prompt on landing page domain, not in native app or business app */}
        {!skipLandingPage && <PWAInstallPrompt />}
        <Routes>
          {/* 
            Domain-based routing:
            - mjenterprises.net → Landing Page
            - app.mjenterprises.net → Login (skip landing page)
            - Native app → Login (skip landing page)
          */}
          <Route path="/" element={skipLandingPage ? <Navigate to="/login" replace /> : <LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/setup-2fa" element={<ProtectedRoute><Setup2FA /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/expenses/new" element={<ProtectedRoute><ExpenseForm /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute><ExpenseList /></ProtectedRoute>} />
          <Route path="/expense-list" element={<ProtectedRoute><ExpenseList /></ProtectedRoute>} />
          <Route path="/credits" element={<ProtectedRoute><CreditList /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/misc" element={<ProtectedRoute><Misc /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute><EmployeeManagement /></ProtectedRoute>} />
          <Route path="/silica-loads" element={<ProtectedRoute><SilicaLoads /></ProtectedRoute>} />
          <Route path="/investment" element={<ProtectedRoute><Investment /></ProtectedRoute>} />
          <Route path="/profit-report" element={<ProtectedRoute><ProfitReport /></ProtectedRoute>} />
          <Route path="/pending-bills" element={<ProtectedRoute><PendingBills /></ProtectedRoute>} />
          <Route path="/auditor" element={<ProtectedRoute><Auditor /></ProtectedRoute>} />
          <Route path="/invoice" element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;